import React, { useEffect, useState } from 'react';
import {
  AlignLeftOutlined,
  AlignRightOutlined,
  CaretDownFilled,
  CaretRightFilled,
  UserOutlined,
  SettingOutlined,
  AppstoreOutlined,
  TagsOutlined,
  ReadOutlined,
  UnorderedListOutlined,
  BookOutlined,
  VideoCameraOutlined,
  FileDoneOutlined,
  TeamOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Dropdown, Layout, Menu, notification, Typography, Modal, Form, Input } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { logoutRequest, changePassword } from '../services/auth';
import { setLogout } from '../store/ducks/user/userSlice';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { LogoutIcon } from '../assets/icon/iconSvg';

dayjs.locale('vi');

const { Sider, Content } = Layout;

const AuthLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dataRedux = useSelector((state) => state.user);
  const [selectKeys, setSelectKeys] = useState([]);
  const [openChangePass, setOpenChangePass] = useState(false);
  const [formChangePass] = Form.useForm();
  useEffect(() => {
    setSelectKeys([location.pathname]);
  }, [location]);

  // Uppercase first letter helper (for Vietnamese weekday like "thứ năm" -> "Thứ năm")
  const ucFirst = (str) => (typeof str === 'string' && str.length > 0 ? str[0].toUpperCase() + str.slice(1) : str);

  const { mutate: mutateLogout } = useMutation({
    mutationFn: logoutRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        await dispatch(setLogout());
        notification.success({ message: 'Đăng xuất thành công!' });
        navigate('/login');
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau.1!',
        });
      }
    },
    onError: (error) => {
      console.log(error);
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau.2!',
      });
    },
  });

  const { mutate: mutateChangePassword, isPending: changingPass } = useMutation({
    mutationFn: changePassword,
    onSuccess: async (data) => {
      if (data?.status === 1 || data?.isSuccess) {
        notification.success({ message: 'Đổi mật khẩu thành công!' });
        setOpenChangePass(false);
        formChangePass.resetFields();
        // Đổi mật khẩu xong, yêu cầu đăng nhập lại
        await dispatch(setLogout());
        navigate('/login');
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra, vui lòng thử lại!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại!' });
    },
  });

  const items = [
    {
      key: '/cms/quan-ly-cau-hinh',
      label: <Link to={'/cms/quan-ly-cau-hinh'}>Quản lý cấu hình</Link>,
      icon: <SettingOutlined />,
    },
    {
      key: '/cms/quan-ly-menu',
      label: <Link to={'/cms/quan-ly-menu'}>Quản lý menu</Link>,
      icon: <AppstoreOutlined />,
    },
    {
      key: '/cms/quan-ly-danh-muc',
      label: <Link to={'/cms/quan-ly-danh-muc'}>Quản lý danh mục</Link>,
      icon: <TagsOutlined />,
    },
    {
      key: '/cms/quan-ly-bai-viet',
      label: <Link to={'/cms/quan-ly-bai-viet'}>Quản lý bài viết</Link>,
      icon: <ReadOutlined />,
    },
    {
      key: '/cms/quan-ly-chuong-trinh',
      label: <Link to={'/cms/quan-ly-chuong-trinh'}>Quản lý chương trình học</Link>,
      icon: <UnorderedListOutlined />,
    },
    {
      key: '/cms/quan-ly-bai-hoc',
      label: <Link to={'/cms/quan-ly-bai-hoc'}>Quản lý bài học</Link>,
      icon: <BookOutlined />,
    },
    {
      key: '/cms/quan-ly-video',
      label: <Link to={'/cms/quan-ly-video'}>Quản lý video</Link>,
      icon: <VideoCameraOutlined />,
    },
    {
      key: '/cms/quan-ly-de-on-tap',
      label: <Link to={'/cms/quan-ly-de-on-tap'}>Quản lý đề ôn tập</Link>,
      icon: <FileDoneOutlined />,
    },
    {
      key: '/cms/quan-ly-hoc-vien',
      label: <Link to={'/cms/quan-ly-hoc-vien'}>Quản lý học viên</Link>,
      icon: <TeamOutlined />,
    },
    {
      key: '/cms/quan-ly-lich-su-on-bai',
      label: <Link to={'/cms/quan-ly-lich-su-on-bai'}>Nhật ký học bài</Link>,
      icon: <FileDoneOutlined />,
    }
  ];

  return (
    <Layout>
      <Sider
        className="fixed top-0 left-0 h-screen overflow-hidden bg-[#075CDD] z-20"
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={256}
        collapsedWidth={80}
      >
        <div className="flex justify-between items-center w-full h-16 px-6 py-4 bg-[#0C2FDD]">
          {!collapsed && <div className="text-2xl text-white font-semibold">Toán cô Hiền</div>}
          <Button
            type="text"
            icon={
              collapsed ? (
                <AlignLeftOutlined className="text-white text-2xl" />
              ) : (
                <AlignRightOutlined className="text-white text-2xl" />
              )
            }
            onClick={() => setCollapsed(!collapsed)}
          />
        </div>
        <Menu
          mode="inline"
          theme="dark"
          expandIcon={({ isOpen }) => (isOpen ? <CaretDownFilled /> : <CaretRightFilled />)}
          className="bg-[#075CDD] gap-4 overflow-y-auto scrollbar-none"
          selectedKeys={selectKeys}
          items={items}
        />
        <div
          onClick={() => mutateLogout()}
          className="absolute bottom-0 left-0 flex justify-start items-center w-full h-12 px-6 py-3 bg-[#0C2FDD] hover:bg-blue-800 z-10 hover:cursor-pointer gap-2"
        >
          <LogoutIcon color="white" />
          {!collapsed && <div className="text-white flex item-center text-sm font-semibold">Đăng xuất</div>}
        </div>
      </Sider>
      <Layout className={`min-h-screen ${!collapsed ? 'ml-64' : 'ml-20'}`}>
        <div className="sticky top-0 z-10 bg-white h-16">
          <div className="flex flex-row w-full h-full justify-between items-center px-6">
            <div className="flex flex-col h-full justify-between items-start py-2">
              <div className="text-[#3699FF] text-base font-semibold">{dayjs().format('DD/MM/YYYY')}</div>
              <div className="text-[#181C32] text-sm font-semibold">{ucFirst(dayjs().format('dddd'))}</div>
            </div>
            <div className="w-full flex justify-end items-center">
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'change_password',
                      icon: <LockOutlined style={{ fontSize: 18, fontWeight: 700 }} />,
                      label: <span className="ml-0 font-medium">Đổi mật khẩu</span>,
                      onClick: () => setOpenChangePass(true),
                    },
                    {
                      key: '1',
                      icon: <LogoutIcon color="black" style={{ fontSize: 18, fontWeight: 700 }}/>,
                      label: <span className="ml-3 font-medium">Đăng xuất</span>,
                      onClick: () => mutateLogout(),
                    },
                  ],
                }}
                placement="bottom"
                overlayClassName={'shadow'}
                dropdownRender={(menu) => (
                  <div style={{ minWidth: 180 }}>
                    {menu}
                  </div>
                )}
              >
                <div className={'cursor-pointer inline-flex items-center gap-1'}>
                  <Typography.Text className={' font-medium mr-1 text-base inline-flex items-center'}>
                    {ucFirst(`${dataRedux?.info?.full_name}`)}
                  </Typography.Text>
                  <Avatar src={dataRedux.info.avatar} icon={<UserOutlined />} size={40} />
                </div>
              </Dropdown>
            </div>
          </div>
        </div>
        <Modal
          title="Đổi mật khẩu"
          open={openChangePass}
          onCancel={() => setOpenChangePass(false)}
          onOk={() => {
            formChangePass
              .validateFields()
              .then((values) => {
                const payload = {
                  old_password: values.old_password,
                  new_password: values.new_password,
                };
                mutateChangePassword(payload);
              })
              .catch(() => {});
          }}
          confirmLoading={changingPass}
          okText="Cập nhật"
          cancelText="Hủy"
          destroyOnClose
        >
          <Form form={formChangePass} layout="vertical">
            <Form.Item
              name="old_password"
              label="Mật khẩu hiện tại"
              rules={[{ required: true, message: 'Vui lòng nhập mật khẩu hiện tại' }]}
            >
              <Input.Password placeholder="Nhập mật khẩu hiện tại" />
            </Form.Item>
            <Form.Item
              name="new_password"
              label="Mật khẩu mới"
              rules={[
                { required: true, message: 'Vui lòng nhập mật khẩu mới' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const hasMin = String(value).length >= 6;
                    const hasUpper = /[A-Z]/.test(value);
                    const hasSpecial = /[^A-Za-z0-9]/.test(value);
                    return hasMin && hasUpper && hasSpecial
                      ? Promise.resolve()
                      : Promise.reject(new Error('Mật khẩu mới phải có ít nhất 6 ký tự, 1 chữ hoa và 1 ký tự đặc biệt'));
                  },
                },
              ]}
            >
              <Input.Password placeholder="Nhập mật khẩu mới" />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="Xác nhận mật khẩu mới"
              dependencies={["new_password"]}
              rules={[
                { required: true, message: 'Vui lòng nhập lại mật khẩu mới' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                    return Promise.reject(new Error('Mật khẩu xác nhận không khớp'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Nhập lại mật khẩu mới" />
            </Form.Item>
          </Form>
        </Modal>
        <Content className="mx-6 my-4 p-6 min-h-72 bg-white rounded-md">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AuthLayout;
